import { useState, useEffect, useRef, useCallback } from 'react';

export type SessionMode = 'flashcard' | 'hardterms' | 'synonym';

export interface SessionTrackerResult {
  isSessionActive: boolean;
  /** Sessions completed + 1 — the session the user is about to start */
  sessionNumber: number;
  lastSessionCardCount: number;
  lifetimeSeenCount: number;
  /** Timestamp (Date.now()) when startSession() was last called — use to compute elapsed */
  sessionStartTimestamp: number | null;
  startSession: () => void;
  /** Call each time a unique word is answered for the first time */
  recordCardSeen: (word: string) => void;
  /** Call when the session ends (component unmount, tab switch, or manual exit) */
  endSession: (cardsThisSession: number) => void;
}

export function useSessionTracker(mode: SessionMode): SessionTrackerResult {
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionsDone, setSessionsDone] = useState(0);
  const [lastSessionCardCount, setLastSessionCardCount] = useState(0);
  const [lifetimeSeenCount, setLifetimeSeenCount] = useState(0);
  const [sessionStartTimestamp, setSessionStartTimestamp] = useState<number | null>(null);

  // Ref so recordCardSeen can mutate without stale-closure issues
  const lifetimeSeenRef = useRef<Set<string>>(new Set());

  // Load persisted values on mount
  useEffect(() => {
    try {
      const count = parseInt(localStorage.getItem(`gre_${mode}_session_count`) ?? '0', 10);
      const last  = parseInt(localStorage.getItem(`gre_${mode}_last_session_cards`) ?? '0', 10);
      const raw   = localStorage.getItem(`gre_${mode}_lifetime_seen`);
      const seen  = raw ? new Set<string>(JSON.parse(raw) as string[]) : new Set<string>();
      lifetimeSeenRef.current = seen;
      setSessionsDone(isNaN(count) ? 0 : count);
      setLastSessionCardCount(isNaN(last)  ? 0 : last);
      setLifetimeSeenCount(seen.size);
    } catch { /* ignore parse errors */ }
  }, [mode]);

  const startSession = useCallback(() => {
    setIsSessionActive(true);
    setSessionStartTimestamp(Date.now());
  }, []);

  const recordCardSeen = useCallback((word: string) => {
    if (lifetimeSeenRef.current.has(word)) return;
    lifetimeSeenRef.current.add(word);
    setLifetimeSeenCount(lifetimeSeenRef.current.size);
    try {
      localStorage.setItem(
        `gre_${mode}_lifetime_seen`,
        JSON.stringify(Array.from(lifetimeSeenRef.current)),
      );
    } catch { /* quota errors are non-fatal */ }
  }, [mode]);

  const endSession = useCallback((cardsThisSession: number) => {
    setSessionsDone(prev => {
      const next = prev + 1;
      try {
        localStorage.setItem(`gre_${mode}_session_count`, next.toString());
        localStorage.setItem(`gre_${mode}_last_session_cards`, cardsThisSession.toString());
      } catch {}
      return next;
    });
    setLastSessionCardCount(cardsThisSession);
    setIsSessionActive(false);
    setSessionStartTimestamp(null);
  }, [mode]);

  return {
    isSessionActive,
    sessionNumber: sessionsDone + 1,
    lastSessionCardCount,
    lifetimeSeenCount,
    sessionStartTimestamp,
    startSession,
    recordCardSeen,
    endSession,
  };
}
